/**
 * importSignatureHtml — arbitrary Gmail signature HTML -> editable signature spec.
 *
 * The inverse of compileSignatureHtml: parses whatever is currently installed
 * in the user's Gmail (generator tables, Gmail's own div-based editor output,
 * hand-written HTML) and reconstructs it as rows/columns/blocks so the result
 * renders IDENTICALLY when re-compiled.
 *
 * Fidelity priorities, in order:
 *   1. Image sources and EXACT effective dimensions (CSS px beats width/height
 *      attributes, max-width caps, % resolved against the container, missing
 *      dimensions resolved from the image's natural size asynchronously).
 *   2. Text content and per-run styling (bold/italic/underline/color/size/
 *      links) via text-block `segments`.
 *   3. Layout structure: table rows/cells -> spec rows/columns (widths,
 *      valign, padding, border-left), social icon rows, buttons, dividers,
 *      vertical gaps.
 *
 * Anything the spec model cannot represent is approximated and reported in
 * `warnings` so the UI (and FutureBot) can tell the user what to double-check.
 *
 * Environment-agnostic: uses DOMParser when available (browser/jsdom); a
 * custom `parseDocument` can be injected for plain node.
 */

import {
  LIMITS,
  sanitizeSpec,
  defaultGlobal,
} from './signatureSpec';
import { getSignatureStats } from './compileSignatureHtml';

// ---------------------------------------------------------------------------
// Small parsing utilities
// ---------------------------------------------------------------------------

const NAMED_COLORS = {
  black: '#000000', white: '#ffffff', gray: '#808080', grey: '#808080',
  silver: '#c0c0c0', red: '#ff0000', navy: '#000080', blue: '#0000ff',
  green: '#008000', orange: '#ffa500', purple: '#800080', maroon: '#800000',
  teal: '#008080', dimgray: '#696969', dimgrey: '#696969', darkgray: '#a9a9a9',
  darkgrey: '#a9a9a9', lightgray: '#d3d3d3', lightgrey: '#d3d3d3',
};

/** Any CSS color -> lowercase #rrggbb, or null when unparseable. */
export function parseColor(raw) {
  const v = String(raw || '').trim().toLowerCase();
  if (!v || v === 'transparent' || v === 'inherit' || v === 'initial' || v === 'currentcolor') return null;
  if (/^#[0-9a-f]{6}$/.test(v)) return v;
  if (/^#[0-9a-f]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  if (/^#[0-9a-f]{8}$/.test(v)) return v.slice(0, 7); // drop alpha
  const rgb = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) {
    const hex = (n) => Math.min(255, parseInt(n, 10)).toString(16).padStart(2, '0');
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
  }
  return NAMED_COLORS[v] || null;
}

/** CSS length -> px number (pt/em/rem resolved against baseFontPx), else null. */
export function parseLength(raw, baseFontPx = 13) {
  const v = String(raw ?? '').trim().toLowerCase();
  if (!v || v === 'auto' || v === 'inherit' || v === 'initial' || v === 'normal') return null;
  const m = v.match(/^(-?[\d.]+)(px|pt|em|rem|%)?$/);
  if (!m) return null;
  if (m[2] === '%') return null; // callers that support % handle it separately
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n)) return null;
  switch (m[2]) {
    case 'pt': return Math.round(n * (4 / 3));
    case 'em':
    case 'rem': return Math.round(n * baseFontPx);
    default: return Math.round(n);
  }
}

function parsePercent(raw) {
  const m = String(raw ?? '').trim().match(/^([\d.]+)%$/);
  return m ? parseFloat(m[1]) / 100 : null;
}

/** Minimal inline-style parser. */
function styleMap(el) {
  const out = {};
  const raw = el.getAttribute && el.getAttribute('style');
  if (!raw) return out;
  for (const part of raw.split(';')) {
    const idx = part.indexOf(':');
    if (idx === -1) continue;
    const key = part.slice(0, idx).trim().toLowerCase();
    const value = part.slice(idx + 1).trim();
    if (key) out[key] = value;
  }
  return out;
}

const FONT_ALIASES = {
  arial: 'arial', helvetica: 'helvetica', 'helvetica neue': 'helvetica',
  georgia: 'georgia', verdana: 'verdana', tahoma: 'tahoma',
  'trebuchet ms': 'trebuchet', trebuchet: 'trebuchet',
  'times new roman': 'times', times: 'times', 'courier new': 'courier',
  courier: 'courier', monospace: 'courier', serif: 'georgia',
  'sans-serif': 'arial', roboto: 'arial', 'open sans': 'arial', lato: 'arial',
  montserrat: 'arial', inter: 'arial', calibri: 'arial', 'segoe ui': 'tahoma',
  garamond: 'georgia', palatino: 'georgia', 'book antiqua': 'georgia',
  'comic sans ms': 'trebuchet', 'century gothic': 'verdana', system: 'arial',
  '-apple-system': 'arial', 'pt sans': 'arial', 'noto sans': 'arial',
};

/** First family of a font-family list -> our fontId (or null = keep inherited). */
export function resolveFontId(fontFamily) {
  const first = String(fontFamily || '').split(',')[0].trim().replace(/^['"]|['"]$/g, '').toLowerCase();
  if (!first) return null;
  return FONT_ALIASES[first] || null;
}

// HTML <font size="1..7"> -> px (legacy mapping).
const FONT_TAG_SIZES = { 1: 10, 2: 13, 3: 16, 4: 18, 5: 24, 6: 32, 7: 48 };

const BLOCK_TAGS = new Set(['div', 'p', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'address', 'section', 'header', 'footer', 'center', 'pre']);
const SKIP_TAGS = new Set(['script', 'style', 'head', 'meta', 'link', 'title', 'noscript', 'template', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea', 'video', 'audio', 'map', 'svg']);

const HEADING_SIZES = { h1: 24, h2: 20, h3: 16, h4: 14, h5: 13, h6: 11 };

// What recipients actually see for unstyled content in Gmail.
const GMAIL_DEFAULT_TEXT = '#222222';
const GMAIL_DEFAULT_LINK = '#1155cc';
const DEFAULT_KEY = '__default__';

// ---------------------------------------------------------------------------
// Inherited-style context
// ---------------------------------------------------------------------------

function rootCtx() {
  return {
    fontId: null,          // resolved web-safe font id (null = unspecified)
    fontSize: 13,          // small(normal-ish) Gmail compose default
    color: null,           // null = client default
    bold: false,
    italic: false,
    underline: null,       // tri-state: null = client default for the node type
    lineHeight: null,
    href: '',
    align: 'left',
    textTransform: null,   // uppercase|lowercase|capitalize — baked into text at import
    letterSpacing: null,   // px
  };
}

/**
 * Bake a CSS text-transform into the text itself (the spec has no
 * text-transform — baking renders identically and stays editable).
 * `prevChar` is the character immediately before this run on the same line,
 * so capitalize doesn't uppercase the middle of a word split across runs.
 */
export function applyTextTransform(text, mode, prevChar = '') {
  if (mode === 'uppercase') return text.toUpperCase();
  if (mode === 'lowercase') return text.toLowerCase();
  if (mode === 'capitalize') {
    const lead = prevChar || ' ';
    return (lead + text)
      .replace(/(^|[^\p{L}\p{N}])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase())
      .slice(lead.length);
  }
  return text;
}

function deriveCtx(ctx, el, state) {
  const next = { ...ctx };
  const tag = el.tagName.toLowerCase();
  const st = styleMap(el);

  if (tag === 'b' || tag === 'strong') next.bold = true;
  if (tag === 'i' || tag === 'em') next.italic = true;
  if (tag === 'u' || tag === 'ins') next.underline = true;
  if (tag === 'small') next.fontSize = Math.max(8, Math.round(next.fontSize * 0.85));
  if (HEADING_SIZES[tag]) {
    next.bold = true;
    next.fontSize = HEADING_SIZES[tag];
  }
  if (tag === 'center') next.align = 'center';

  if (tag === 'font') {
    const face = el.getAttribute('face');
    if (face) {
      const id = resolveFontId(face);
      if (id) next.fontId = id;
      else state.noteFontFallback(face);
    }
    const colorAttr = parseColor(el.getAttribute('color'));
    if (colorAttr) next.color = colorAttr;
    const sizeAttr = parseInt(el.getAttribute('size'), 10);
    if (FONT_TAG_SIZES[sizeAttr]) next.fontSize = FONT_TAG_SIZES[sizeAttr];
  }

  if (tag === 'a') {
    const href = (el.getAttribute('href') || '').trim();
    if (href && !/^(javascript|vbscript|data):/i.test(href)) next.href = href;
    else if (href) state.warn('A link using an unsafe URL scheme was removed.');
    // Browsers underline links by default; explicit styles below override.
    if (next.underline === null) next.underline = true;
  }

  if (st['font-family']) {
    const id = resolveFontId(st['font-family']);
    if (id) next.fontId = id;
    else state.noteFontFallback(st['font-family']);
  }
  if (st['font-size']) {
    const px = parseLength(st['font-size'], ctx.fontSize);
    if (px) next.fontSize = px;
    else {
      const pct = parsePercent(st['font-size']);
      if (pct) next.fontSize = Math.round(ctx.fontSize * pct);
    }
  }
  if (st.color) {
    const c = parseColor(st.color);
    if (c) next.color = c;
  }
  if (st['font-weight']) {
    const w = st['font-weight'].trim().toLowerCase();
    const n = parseInt(w, 10);
    if (w === 'bold' || w === 'bolder' || n >= 600) next.bold = true;
    else if (w === 'normal' || (n > 0 && n < 600)) next.bold = false;
  }
  if (st['font-style']) {
    const fs = st['font-style'].trim().toLowerCase();
    if (fs.includes('italic') || fs.includes('oblique')) next.italic = true;
    else if (fs === 'normal') next.italic = false;
  }
  if (st['text-decoration'] || st['text-decoration-line']) {
    const deco = `${st['text-decoration'] || ''} ${st['text-decoration-line'] || ''}`.toLowerCase();
    if (deco.includes('underline')) next.underline = true;
    else if (deco.includes('none')) next.underline = false;
  }
  if (st['line-height']) {
    const lh = st['line-height'].trim().toLowerCase();
    if (/^[\d.]+$/.test(lh)) next.lineHeight = Math.round(parseFloat(lh) * next.fontSize);
    else if (parsePercent(lh) != null) next.lineHeight = Math.round(parsePercent(lh) * next.fontSize);
    else {
      const px = parseLength(lh, next.fontSize);
      if (px) next.lineHeight = px;
    }
  }
  if (st['text-align'] && ['left', 'center', 'right'].includes(st['text-align'].trim())) {
    next.align = st['text-align'].trim();
  }
  const alignAttr = (el.getAttribute('align') || '').toLowerCase();
  if (['left', 'center', 'right'].includes(alignAttr)) next.align = alignAttr;

  if (st['text-transform']) {
    const tt = st['text-transform'].trim().toLowerCase();
    if (tt === 'uppercase' || tt === 'lowercase' || tt === 'capitalize') next.textTransform = tt;
    else if (tt === 'none') next.textTransform = null;
  }
  if (st['letter-spacing']) {
    const ls = st['letter-spacing'].trim().toLowerCase();
    if (ls === 'normal') next.letterSpacing = null;
    else {
      const px = parseLength(ls, next.fontSize);
      next.letterSpacing = px && px > 0 ? px : null;
    }
  }

  return next;
}

// ---------------------------------------------------------------------------
// Image extraction — the fidelity-critical path
// ---------------------------------------------------------------------------

/**
 * Effective rendered dimensions of an <img>, per browser precedence:
 * inline CSS width/height beat the width/height attributes; max-width caps.
 * width/height may come back null (= resolve from natural size later).
 */
export function extractImageDims(el, ctx, containerWidth) {
  const st = styleMap(el);
  const attr = (name) => {
    const raw = el.getAttribute(name);
    if (raw == null || raw === '') return { px: null, pct: null };
    const pct = parsePercent(raw);
    if (pct != null) return { px: null, pct };
    const n = parseInt(raw, 10);
    return { px: Number.isFinite(n) && n > 0 ? n : null, pct: null };
  };
  const css = (name) => {
    if (st[name] == null) return { px: null, pct: null, set: false };
    const pct = parsePercent(st[name]);
    if (pct != null) return { px: null, pct, set: true };
    const px = parseLength(st[name], ctx.fontSize);
    return { px: px && px > 0 ? px : null, pct: null, set: true };
  };

  const wAttr = attr('width');
  const hAttr = attr('height');
  const wCss = css('width');
  const hCss = css('height');

  let width = wCss.set ? wCss.px : wAttr.px;
  let height = hCss.set ? hCss.px : hAttr.px;
  let widthPct = wCss.set ? wCss.pct : wAttr.pct;

  // max-width caps whatever width wins (or the natural width when none set).
  let maxW = null;
  if (st['max-width']) {
    const mPct = parsePercent(st['max-width']);
    if (mPct != null && containerWidth) maxW = Math.round(mPct * containerWidth);
    else maxW = parseLength(st['max-width'], ctx.fontSize);
  }

  if (widthPct != null && containerWidth) {
    width = Math.round(widthPct * containerWidth);
    widthPct = null;
  }

  let pendingMaxWidth = null;
  if (maxW) {
    if (width != null && width > maxW) {
      if (height != null) height = Math.max(1, Math.round(height * (maxW / width)));
      width = maxW;
    } else if (width == null) {
      pendingMaxWidth = maxW; // caps the (unknown) natural width
    }
  }

  const radius = st['border-radius'] || '';
  const rPct = parsePercent(radius);
  const rPx = parseLength(radius, ctx.fontSize);
  const circle = rPct != null ? rPct >= 0.5 : (rPx != null && width != null && rPx >= width / 2);
  const partialRadius = !circle && rPx != null && rPx > 0;

  return {
    width: width || null,
    height: height || null,
    pendingMaxWidth,
    pendingPercent: widthPct,
    circle,
    partialRadius,
  };
}

function imageFromEl(el, ctx, state, containerWidth) {
  const src = (el.getAttribute('src') || '').trim();
  if (!src) return null;
  if (/^data:/i.test(src)) {
    state.warn('An embedded (base64) image was skipped — Gmail strips those; upload it to the image library instead.');
    return null;
  }
  if (/^http:\/\//i.test(src)) {
    state.warn('An image used insecure http:// and was upgraded to https:// — verify it still loads.');
  } else if (!/^https:\/\//i.test(src)) {
    state.warn('An image with a non-web (relative or unsupported) source was skipped.');
    return null;
  }
  const dims = extractImageDims(el, ctx, containerWidth);
  if (dims.partialRadius) {
    state.warn('An image had partially rounded corners — the Studio supports square or fully circular images, so it was imported square.');
  }
  const block = {
    type: 'image',
    src: src.replace(/^http:/i, 'https:'),
    alt: (el.getAttribute('alt') || '').slice(0, 120),
    width: dims.width,
    height: dims.height,
    circle: dims.circle,
    href: ctx.href || '',
    align: ctx.align !== 'left' ? ctx.align : 'left',
    spacingTop: 0,
  };
  if (dims.pendingPercent != null) {
    state.pendingImages.push({ block, percent: dims.pendingPercent });
  } else if (dims.pendingMaxWidth) {
    state.pendingImages.push({ block, maxWidth: dims.pendingMaxWidth });
  } else if (block.width == null || block.height == null) {
    state.pendingImages.push({ block });
  }
  return block;
}

// ---------------------------------------------------------------------------
// Flow walking: element content -> lines of runs/images -> blocks
// ---------------------------------------------------------------------------

function normalizeText(raw) {
  // Collapse HTML whitespace but preserve intentional &nbsp; (\u00a0).
  return String(raw || '').replace(/[\r\n\t\f ]+/g, ' ');
}

function sameRunStyle(a, b) {
  return a.bold === b.bold && a.italic === b.italic && a.underline === b.underline
    && (a.color || '') === (b.color || '') && (a.size || 0) === (b.size || 0)
    && (a.href || '') === (b.href || '')
    && (a.letterSpacing || 0) === (b.letterSpacing || 0);
}

/**
 * Collects inline runs/images into "lines" (split on <br> and block-element
 * boundaries), then converts each line into spec block(s).
 */
class ColumnBuilder {
  constructor(state, containerWidth) {
    this.state = state;
    this.containerWidth = containerWidth || null;
    this.blocks = [];
    this.line = [];      // [{ kind: 'run', text, style } | { kind: 'img', block }]
    this.lineCtxs = [];
    this.pendingGap = 0; // px of vertical space to attach to the next block
    this.blankLines = 0;
  }

  addText(text, ctx) {
    let t = normalizeText(text);
    if (!t) return;
    if (!t.trim() && !this.line.length) return; // leading whitespace on a line
    if (ctx.textTransform) {
      const prevAtom = this.line[this.line.length - 1];
      const prevChar = prevAtom && prevAtom.kind === 'run' ? prevAtom.text.slice(-1) : '';
      t = applyTextTransform(t, ctx.textTransform, prevChar);
    }
    if (t.trim()) this.state.tallyFont(ctx.fontId, t.trim().length);
    this.line.push({
      kind: 'run',
      text: t,
      style: {
        bold: ctx.bold,
        italic: ctx.italic,
        underline: ctx.underline === true,
        color: ctx.color || '',
        size: ctx.fontSize || null,
        href: ctx.href || '',
        letterSpacing: ctx.letterSpacing || 0,
      },
    });
    this.lineCtxs.push(ctx);
  }

  addImage(el, ctx) {
    const src = (el.getAttribute('src') || '').trim();
    if (!src) {
      // Srcless <img height="N"> is a classic email spacer hack — the image
      // renders as nothing but its box keeps the vertical space. Preserve it.
      const st = styleMap(el);
      const h = parseLength(st.height, ctx.fontSize) ?? parseLength(el.getAttribute('height'), ctx.fontSize);
      if (h > 1) {
        this.endLine();
        this.pushBlock({ type: 'spacer', height: Math.max(2, Math.min(Math.round(h), LIMITS.spacing.max)) }, ctx);
      }
      return;
    }
    const block = imageFromEl(el, ctx, this.state, this.containerWidth);
    if (block) {
      this.line.push({ kind: 'img', block });
      this.lineCtxs.push(ctx);
    }
  }

  addGap(px) {
    this.pendingGap += Math.max(0, Math.round(px || 0));
  }

  /** Attach horizontal spacing after the most recent atom (inter-cell padding in inlined nested tables). */
  padLastAtom(px) {
    const p = Math.round(px || 0);
    if (p <= 0 || !this.line.length) return;
    const lastAtom = this.line[this.line.length - 1];
    if (lastAtom.kind === 'img') lastAtom.block.paddingRight = (lastAtom.block.paddingRight || 0) + p;
    else lastAtom.paddingRight = (lastAtom.paddingRight || 0) + p;
  }

  pushBlock(block, ctx) {
    if (!block) return;
    if (this.pendingGap > 0) {
      const gap = this.pendingGap;
      this.pendingGap = 0;
      if (gap <= LIMITS.spacing.max) {
        block.spacingTop = (block.spacingTop || 0) + gap;
      } else {
        this.blocks.push({ type: 'spacer', height: LIMITS.spacing.max });
        this.state.warn(`A vertical gap of ${gap}px was clamped to ${LIMITS.spacing.max}px.`);
      }
    }
    if (ctx && ctx.align && ctx.align !== 'left' && block.type !== 'social' && !block.align) {
      block.align = ctx.align;
    }
    this.blocks.push(block);
  }

  /**
   * End the current visual line, emitting block(s). Structural flushes (block
   * element boundaries) of an empty buffer are no-ops; only a <br> that ends
   * an already-empty line counts as a blank line (brTriggered=true).
   */
  endLine(brTriggered = false) {
    const atoms = this.line;
    const ctxs = this.lineCtxs;
    this.line = [];
    this.lineCtxs = [];

    const hasContent = atoms.some((a) => (a.kind === 'run' ? a.text.trim().length : true));
    if (!hasContent) {
      if (brTriggered && this.blocks.length) this.blankLines += 1; // leading blanks dropped
      return;
    }

    // Convert accumulated blank lines into a gap (~1 line height each).
    if (this.blankLines > 0) {
      const c = ctxs[0];
      const lh = (c && (c.lineHeight || Math.round(c.fontSize * 1.2))) || 16;
      this.addGap(this.blankLines * lh);
      this.blankLines = 0;
    }

    const imgs = atoms.filter((a) => a.kind === 'img');
    const hasText = atoms.some((a) => a.kind === 'run' && a.text.trim().length);
    const lineCtx = ctxs[0] || rootCtx();

    // A row of ≥2 small LINKED images with no text = a social/icon bar.
    if (imgs.length >= 2 && !hasText) {
      const social = this.socialFromImages(imgs);
      if (social) {
        this.pushBlock(social, lineCtx);
        return;
      }
    }

    if (imgs.length && (hasText || imgs.length >= 2)) {
      // Icon-sized images flow INLINE with the text (contact rows like
      // "📞 555-0123", logo strips) — render them as image segments so the
      // line reproduces exactly. Large images can't inline; stack + warn.
      const iconSized = imgs.every(({ block }) =>
        (block.width != null && block.width <= LIMITS.iconSize.max)
        || (block.height != null && block.height <= LIMITS.iconSize.max));
      if (iconSized) {
        this.pushBlock(this.textBlockFromAtoms(atoms, lineCtx), lineCtx);
        return;
      }
      this.state.warn(hasText
        ? 'A line mixed a large image with text side by side — the Studio stacks them (image above text), so that spot may differ.'
        : 'Side-by-side images on one line were stacked vertically — check that section.');
    }

    for (const { block } of imgs) this.pushBlock(block, lineCtx);
    const runAtoms = atoms.filter((a) => a.kind === 'run');
    if (hasText) this.pushBlock(this.textBlockFromAtoms(runAtoms, lineCtx), lineCtx);
  }

  /** A line of ≥2 small linked images = a social/icon bar. */
  socialFromImages(imgs) {
    const iconable = imgs.every(({ block }) => block.href
      && (block.width == null || block.width <= LIMITS.iconSize.max)
      && (block.height == null || block.height <= LIMITS.iconSize.max));
    if (!iconable) return null;
    const kept = imgs.slice(0, LIMITS.maxSocialIcons);
    if (imgs.length > LIMITS.maxSocialIcons) {
      this.state.warn(`An icon row had ${imgs.length} icons — only the first ${LIMITS.maxSocialIcons} were kept.`);
    }
    const size = Math.max(...kept.map(({ block }) => block.width || block.height || 20));
    // Icons render square at `size`; drop any pending dimension resolution.
    this.state.pendingImages = this.state.pendingImages.filter((p) => !imgs.some((i) => i.block === p.block));
    return {
      type: 'social',
      icons: kept.map(({ block }) => ({
        network: 'custom',
        label: block.alt || 'Link',
        href: block.href,
        iconUrl: block.src,
      })),
      size: Math.min(Math.max(size, LIMITS.iconSize.min), LIMITS.iconSize.max),
      gap: 8,
      spacingTop: 0,
    };
  }

  /**
   * One visual line of atoms (text runs and/or icon-sized images) -> a text
   * block. Pure single-style text stays a plain block; anything mixed becomes
   * segments, with images as inline image segments in their original spot.
   */
  textBlockFromAtoms(atoms, lineCtx) {
    // Interleave into entries, attaching whitespace-only runs to the nearest
    // text ("…Broker</span> <span>NMLS…" keeps its inter-span space).
    const entries = [];
    let pendingWs = '';
    for (const a of atoms) {
      if (a.kind === 'img') {
        entries.push({ image: a.block, paddingRight: a.block.paddingRight || 0 });
        continue;
      }
      if (!a.text.trim().length) {
        const prev = entries[entries.length - 1];
        if (prev && prev.text != null && !prev.paddingRight) prev.text += a.text;
        else pendingWs += a.text;
        continue;
      }
      const prev = entries[entries.length - 1];
      const text = pendingWs + a.text;
      pendingWs = '';
      if (prev && prev.text != null && !prev.paddingRight && sameRunStyle(prev.style, a.style)) {
        prev.text += text;
        prev.paddingRight = a.paddingRight || 0;
      } else {
        entries.push({ text, style: { ...a.style }, paddingRight: a.paddingRight || 0 });
      }
    }

    // Trim outer whitespace across the line.
    const first = entries[0];
    if (first && first.text != null) first.text = first.text.replace(/^ +/, '');
    const last = entries[entries.length - 1];
    if (last && last.text != null) last.text = last.text.replace(/ +$/, '');

    // Long paragraphs (legal disclaimers) exceed the per-segment sanitize cap
    // — chunk at word boundaries so no characters are truncated. Chunks are
    // adjacent inline spans, so the rendered flow is unchanged.
    const chunked = [];
    for (const m of entries) {
      if (m.text == null || m.text.length <= LIMITS.segmentTextLength) {
        chunked.push(m);
        continue;
      }
      let t = m.text;
      while (t.length > LIMITS.segmentTextLength) {
        let cut = t.lastIndexOf(' ', LIMITS.segmentTextLength);
        if (cut < LIMITS.segmentTextLength / 2) cut = LIMITS.segmentTextLength;
        chunked.push({ text: t.slice(0, cut), style: m.style, paddingRight: 0 });
        t = t.slice(cut);
      }
      chunked.push({ text: t, style: m.style, paddingRight: m.paddingRight });
    }
    if (chunked.length > LIMITS.maxSegments) {
      this.state.warn('A very long paragraph exceeded the Studio\'s capacity and was shortened — check the fine print.');
    }
    const merged = chunked;

    // Tally EFFECTIVE colors/sizes for global inference (weighted by chars).
    // Unstyled content renders in Gmail's defaults, so tally those values.
    for (const m of merged) {
      if (m.text == null) continue;
      const w = m.text.trim().length;
      this.state.tallySize(m.style.size, w);
      if (m.style.href) this.state.tallyLinkColor(m.style.color || GMAIL_DEFAULT_LINK, w);
      else this.state.tallyTextColor(m.style.color || GMAIL_DEFAULT_TEXT, w);
    }

    // Browsers render `line-height: normal` ≈ 1.2× the font size; our
    // compiler defaults to size+5. Carry an explicit value so line spacing
    // matches the original exactly.
    const textEntries = merged.filter((m) => m.text != null);
    const maxSize = Math.max(...textEntries.map((m) => m.style.size || 0), 0) || lineCtx.fontSize;
    const base = {
      type: 'text',
      spacingTop: 0,
      lineHeight: lineCtx.lineHeight || Math.round(maxSize * 1.2),
    };

    if (merged.length === 1 && merged[0].text != null) {
      const { text, style } = merged[0];
      return {
        ...base,
        text,
        bold: style.bold,
        italic: style.italic,
        underline: style.underline,
        size: style.size,
        color: style.color || '',
        href: style.href || '',
        ...(style.letterSpacing ? { letterSpacing: style.letterSpacing } : {}),
      };
    }

    // Mixed styling -> segments. Block size = the largest run (it drives the
    // line box, like in the browser); styling lives on the runs.
    return {
      ...base,
      text: textEntries.map((m) => m.text).join(''),
      size: maxSize,
      segments: merged.map((m) => {
        if (m.image != null) {
          const seg = {
            image: {
              src: m.image.src,
              width: m.image.width,
              height: m.image.height,
              ...(m.image.href ? { href: m.image.href } : {}),
              ...(m.image.alt ? { alt: m.image.alt } : {}),
            },
            ...(m.paddingRight ? { paddingRight: m.paddingRight } : {}),
          };
          // Natural-size resolution must now fill the SEGMENT's image object.
          this.state.retargetPending(m.image, seg.image);
          return seg;
        }
        return {
          text: m.text,
          bold: m.style.bold,
          italic: m.style.italic,
          underline: m.style.underline,
          ...(m.style.color ? { color: m.style.color } : {}),
          ...(m.style.size && m.style.size !== maxSize ? { size: m.style.size } : {}),
          ...(m.style.href ? { href: m.style.href } : {}),
          ...(m.style.letterSpacing ? { letterSpacing: m.style.letterSpacing } : {}),
          ...(m.paddingRight ? { paddingRight: m.paddingRight } : {}),
        };
      }),
    };
  }

  finish() {
    this.endLine();
    return this.blocks;
  }
}

/** True when an element renders as its own line (block-level). */
function isBlockEl(el) {
  const tag = el.tagName.toLowerCase();
  const st = styleMap(el);
  if ((st.display || '') === 'block') return true;
  if (BLOCK_TAGS.has(tag)) return !(st.display || '').includes('inline');
  return false;
}

/** Vertical margins + padding of a block element in px (both become gaps). */
function blockMargins(el, ctx) {
  const st = styleMap(el);
  const tag = el.tagName.toLowerCase();
  const shorthand = (prop) => {
    if (!st[prop]) return [null, null];
    const parts = st[prop].split(/\s+/).map((p) => parseLength(p, ctx.fontSize) || 0);
    if (!parts.length) return [null, null];
    return [parts[0], parts.length >= 3 ? parts[2] : parts[0]];
  };
  let [top, bottom] = shorthand('margin');
  const [padTop, padBottom] = shorthand('padding');
  if (st['margin-top'] != null) top = parseLength(st['margin-top'], ctx.fontSize) || 0;
  if (st['margin-bottom'] != null) bottom = parseLength(st['margin-bottom'], ctx.fontSize) || 0;
  // <p> has ~1em default margins unless explicitly set.
  if (tag === 'p') {
    if (top == null) top = Math.round(ctx.fontSize);
    if (bottom == null) bottom = Math.round(ctx.fontSize);
  }
  let pTop = padTop;
  let pBottom = padBottom;
  if (st['padding-top'] != null) pTop = parseLength(st['padding-top'], ctx.fontSize) || 0;
  if (st['padding-bottom'] != null) pBottom = parseLength(st['padding-bottom'], ctx.fontSize) || 0;
  return { top: (top || 0) + (pTop || 0), bottom: (bottom || 0) + (pBottom || 0) };
}

/** Detect an <a> styled as a button (background + padding). */
function isButtonLink(el) {
  if (el.tagName.toLowerCase() !== 'a') return false;
  const st = styleMap(el);
  const bg = parseColor(st['background-color'] || st.background);
  return Boolean(bg && (st.padding || st['padding-left'] || st['padding-top']));
}

function buttonFromLink(el, ctx, state) {
  const st = styleMap(el);
  const href = (el.getAttribute('href') || '').trim();
  if (/^(javascript|vbscript|data):/i.test(href)) {
    state.warn('A button used an unsafe URL and was skipped.');
    return null;
  }
  const pad = (st.padding || '').split(/\s+/).map((p) => parseLength(p, ctx.fontSize) || 0);
  const paddingY = pad[0] || parseLength(st['padding-top'], ctx.fontSize) || 8;
  const paddingX = (pad.length > 1 ? pad[1] : pad[0]) || parseLength(st['padding-left'], ctx.fontSize) || 14;
  return {
    type: 'button',
    text: normalizeText(el.textContent).trim().slice(0, 60) || 'Button',
    href,
    bg: parseColor(st['background-color'] || st.background) || '#0f4c81',
    color: parseColor(st.color) || '#ffffff',
    radius: Math.min(parseLength(st['border-radius'], ctx.fontSize) || 0, LIMITS.radius.max),
    paddingX: Math.min(Math.max(paddingX, 4), 40),
    paddingY: Math.min(Math.max(paddingY, 2), 24),
    size: parseLength(st['font-size'], ctx.fontSize) || ctx.fontSize,
    align: ctx.align !== 'left' ? ctx.align : 'left',
    spacingTop: 0,
  };
}

/** Detect a divider: <hr>, or a contentless element that is just a border. */
function dividerFromEl(el, ctx) {
  const tag = el.tagName.toLowerCase();
  const st = styleMap(el);
  if (tag === 'hr') {
    const shorthand = st['border-top'] || st.border || '';
    const m = shorthand.match(/([\d.]+)px\s+\w+\s+(.+)/);
    return {
      type: 'divider',
      color: (m && parseColor(m[2]))
        || parseColor(st['border-color'] || st['border-top-color'] || st['background-color'])
        || '#dddddd',
      thickness: (m && Math.max(1, Math.round(parseFloat(m[1]))))
        || parseLength(st['border-top-width'] || st.height, ctx.fontSize)
        || 1,
      spacingTop: 0,
      spacingBottom: 0,
    };
  }
  const border = st['border-top'] || st['border-bottom'];
  if (border && !normalizeText(el.textContent).trim() && !(el.querySelector && el.querySelector('img,table'))) {
    const parsed = parseBorder(border);
    if (parsed) {
      return { type: 'divider', ...parsed, spacingTop: 0, spacingBottom: 0 };
    }
  }
  return null;
}

/** Parse a CSS border shorthand ("1px solid #ebebeb") into {color, thickness}. */
function parseBorder(str) {
  if (!str) return null;
  const m = str.match(/([\d.]+)px\s+\w+\s+(.+)/);
  if (!m) return null;
  const color = parseColor(m[2]);
  if (!color) return null;
  return { color, thickness: Math.max(1, Math.round(parseFloat(m[1]))) };
}

/**
 * Walk element children as inline flow, feeding a ColumnBuilder.
 * Nested tables are dispatched to handleNestedTable.
 */
function walkFlow(el, ctx, builder, state) {
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === 3) { // text node
      builder.addText(node.textContent, ctx);
      continue;
    }
    if (node.nodeType !== 1) continue;
    const tag = node.tagName.toLowerCase();
    if (SKIP_TAGS.has(tag)) continue;

    if (tag === 'br') {
      builder.endLine(true);
      continue;
    }
    if (tag === 'img') {
      builder.addImage(node, ctx);
      continue;
    }
    if (tag === 'hr') {
      builder.endLine();
      builder.pushBlock(dividerFromEl(node, ctx), ctx);
      continue;
    }
    if (tag === 'table') {
      builder.endLine();
      const tSt = styleMap(node);
      const mTop = parseLength(tSt['margin-top'], ctx.fontSize) || (tSt.margin ? parseLength(tSt.margin.split(/\s+/)[0], ctx.fontSize) : 0) || 0;
      const mBottom = parseLength(tSt['margin-bottom'], ctx.fontSize) || 0;
      if (mTop) builder.addGap(mTop);
      handleNestedTable(node, ctx, builder, state);
      if (mBottom) builder.addGap(mBottom);
      continue;
    }

    const childCtx = deriveCtx(ctx, node, state);

    if (isButtonLink(node)) {
      builder.endLine();
      builder.pushBlock(buttonFromLink(node, childCtx, state), ctx);
      continue;
    }

    if (isBlockEl(node)) {
      builder.endLine();
      const margins = blockMargins(node, ctx);
      if (margins.top) builder.addGap(margins.top);
      const divider = dividerFromEl(node, childCtx);
      if (divider) {
        builder.pushBlock(divider, childCtx);
      } else {
        const st = styleMap(node);
        const explicitH = parseLength(st.height, ctx.fontSize);
        const isEmpty = !normalizeText(node.textContent).trim() && !(node.querySelector && node.querySelector('img,table,hr'));
        if (isEmpty) {
          builder.addGap(explicitH || childCtx.lineHeight || Math.round(childCtx.fontSize * 1.2));
        } else {
          if (tag === 'li') builder.addText('• ', childCtx);
          walkFlow(node, childCtx, builder, state);
          builder.endLine();
        }
      }
      if (margins.bottom) builder.addGap(margins.bottom);
      continue;
    }

    // Inline element (span/a/b/font/...) — recurse with derived styling.
    walkFlow(node, childCtx, builder, state);
  }
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

function tableRows(tableEl) {
  // Direct rows only (thead/tbody/tfoot flattened); nested-table rows excluded.
  const rows = [];
  for (const section of Array.from(tableEl.children)) {
    const tag = section.tagName.toLowerCase();
    if (tag === 'tr') rows.push(section);
    else if (['thead', 'tbody', 'tfoot'].includes(tag)) {
      for (const tr of Array.from(section.children)) {
        if (tr.tagName.toLowerCase() === 'tr') rows.push(tr);
      }
    }
  }
  return rows;
}

function cellsOf(tr) {
  return Array.from(tr.children).filter((c) => ['td', 'th'].includes(c.tagName.toLowerCase()));
}

/** A single row of small linked images -> social bar block. */
function detectSocialTable(tableEl, ctx, state) {
  const rows = tableRows(tableEl);
  if (rows.length !== 1) return null;
  const cells = cellsOf(rows[0]);
  if (cells.length < 2 || cells.length > 10) return null;
  const icons = [];
  let gap = 8;
  for (const cell of cells) {
    if (normalizeText(cell.textContent).trim()) return null;
    const imgs = cell.getElementsByTagName('img');
    if (imgs.length !== 1) return null;
    const img = imgs[0];
    const dims = extractImageDims(img, ctx, null);
    const size = dims.width || dims.height;
    if (size != null && size > LIMITS.iconSize.max) return null;
    const link = img.closest ? img.closest('a') : null;
    const href = link ? (link.getAttribute('href') || '').trim() : '';
    if (!href || /^(javascript|vbscript|data):/i.test(href)) return null;
    const src = (img.getAttribute('src') || '').trim();
    if (!/^https?:\/\//i.test(src)) return null;
    const st = styleMap(cell);
    const padRight = parseLength(st['padding-right'], ctx.fontSize);
    if (padRight != null) gap = padRight;
    icons.push({
      network: 'custom',
      label: (img.getAttribute('alt') || 'Link').slice(0, 40),
      href,
      iconUrl: src.replace(/^http:/i, 'https:'),
      _size: size,
    });
  }
  const kept = icons.slice(0, LIMITS.maxSocialIcons);
  if (icons.length > LIMITS.maxSocialIcons) {
    state.warn(`An icon row had ${icons.length} icons — only the first ${LIMITS.maxSocialIcons} were kept.`);
  }
  const size = Math.max(...kept.map((i) => i._size || 0)) || 20;
  kept.forEach((i) => delete i._size);
  return {
    type: 'social',
    icons: kept,
    size: Math.min(Math.max(size, LIMITS.iconSize.min), LIMITS.iconSize.max),
    gap: Math.min(Math.max(gap, 0), 24),
    spacingTop: 0,
  };
}

/** A one-cell table with a background + single link -> button block. */
function detectButtonTable(tableEl, ctx) {
  const rows = tableRows(tableEl);
  if (rows.length !== 1) return null;
  const cells = cellsOf(rows[0]);
  if (cells.length !== 1) return null;
  const cell = cells[0];
  const st = styleMap(cell);
  const bg = parseColor(st['background-color'] || cell.getAttribute('bgcolor'));
  if (!bg) return null;
  const links = cell.getElementsByTagName('a');
  if (links.length !== 1) return null;
  const a = links[0];
  const href = (a.getAttribute('href') || '').trim();
  if (!href || /^(javascript|vbscript|data):/i.test(href)) return null;
  const aSt = styleMap(a);
  const padParts = (st.padding || '').split(/\s+/).map((p) => parseLength(p, ctx.fontSize) || 0);
  return {
    type: 'button',
    text: normalizeText(a.textContent).trim().slice(0, 60) || 'Button',
    href,
    bg,
    color: parseColor(aSt.color) || '#ffffff',
    radius: Math.min(parseLength(st['border-radius'], ctx.fontSize) || 0, LIMITS.radius.max),
    paddingX: Math.min(Math.max((padParts.length > 1 ? padParts[1] : padParts[0]) || parseLength(st['padding-left'], ctx.fontSize) || 14, 4), 40),
    paddingY: Math.min(Math.max(padParts[0] || parseLength(st['padding-top'], ctx.fontSize) || 8, 2), 24),
    size: parseLength(aSt['font-size'], ctx.fontSize) || ctx.fontSize,
    align: ctx.align !== 'left' ? ctx.align : 'left',
    spacingTop: 0,
  };
}

/** A nested table inside a cell: social bar, button, flatten, or approximate. */
function handleNestedTable(tableEl, ctx, builder, state) {
  const social = detectSocialTable(tableEl, ctx, state);
  if (social) {
    builder.pushBlock(social, ctx);
    return;
  }
  const button = detectButtonTable(tableEl, ctx);
  if (button) {
    builder.pushBlock(button, ctx);
    return;
  }
  const rows = tableRows(tableEl);
  const tableCtx = deriveCtx(ctx, tableEl, state);

  // Multi-column nested rows whose cells hold only inline content (icons,
  // short text, links — the classic "📞 | 555-0123 | ✉ | email" contact row)
  // flow onto ONE line, reproducing the original left-to-right layout with
  // inter-cell padding. Anything heavier falls back to vertical stacking.
  const inlineSafe = rows.every((tr) => {
    const cells = cellsOf(tr);
    if (cells.length <= 1) return true;
    return cells.every((cell) => isInlineSafeCell(cell));
  });
  if (!inlineSafe && rows.some((tr) => cellsOf(tr).length > 1)) {
    state.warn('A nested multi-column table could not be fully preserved — its cells were stacked vertically. Review that section.');
  }

  for (const tr of rows) {
    const cells = cellsOf(tr);
    if (inlineSafe && cells.length > 1) {
      builder.endLine();
      cells.forEach((cell, i) => {
        const cellCtx = deriveCtx(tableCtx, cell, state);
        const st = styleMap(cell);
        walkFlow(cell, cellCtx, builder, state);
        const gapAfter = (parseLength(st['padding-right'], ctx.fontSize) || 0)
          + (cells[i + 1] ? (parseLength(styleMap(cells[i + 1])['padding-left'], ctx.fontSize) || 0) : 0);
        if (cells[i + 1]) builder.padLastAtom(gapAfter);
      });
      builder.endLine();
      continue;
    }
    for (const cell of cells) {
      const cellCtx = deriveCtx(tableCtx, cell, state);
      const st = styleMap(cell);
      const divider = dividerFromEl(cell, cellCtx);
      if (divider) {
        builder.endLine();
        builder.pushBlock(divider, cellCtx);
        continue;
      }
      const padTop = parseLength(st['padding-top'], ctx.fontSize) || 0;
      if (padTop) builder.addGap(padTop);
      const heightAttr = parseLength(st.height, ctx.fontSize) || parseLength(cell.getAttribute('height'), ctx.fontSize);
      const isEmpty = !normalizeText(cell.textContent).trim() && !cell.querySelector('img,table');
      if (isEmpty && heightAttr) {
        builder.addGap(heightAttr);
        continue;
      }
      walkFlow(cell, cellCtx, builder, state);
      builder.endLine();
      const padBottom = parseLength(st['padding-bottom'], ctx.fontSize) || 0;
      if (padBottom) builder.addGap(padBottom);
    }
  }
}

/**
 * A nested-table cell that can flow inline: only text/inline elements and
 * icon-sized images — no tables, line breaks, block elements, or long copy.
 */
function isInlineSafeCell(cell) {
  if (cell.querySelector('table,div,p,br,hr,ul,ol,blockquote')) return false;
  if (normalizeText(cell.textContent).trim().length > 120) return false;
  for (const img of Array.from(cell.querySelectorAll('img'))) {
    if (!(img.getAttribute('src') || '').trim()) continue; // spacer hack — ignored
    const st = styleMap(img);
    const w = parseLength(st.width, 13) ?? parseLength(img.getAttribute('width'), 13);
    const h = parseLength(st.height, 13) ?? parseLength(img.getAttribute('height'), 13);
    const pinnedSmall = (w != null && w <= LIMITS.iconSize.max) || (h != null && h <= LIMITS.iconSize.max);
    if (!pinnedSmall) return false;
  }
  return true;
}

/** The single <table> child of a cell that has no other content, or null. */
function soleChildTable(cell) {
  let table = null;
  for (const n of Array.from(cell.childNodes)) {
    if (n.nodeType === 3) {
      if (normalizeText(n.textContent).trim()) return null;
      continue;
    }
    if (n.nodeType !== 1) continue;
    if (n.tagName.toLowerCase() === 'table') {
      if (table) return null;
      table = n;
      continue;
    }
    // Contentless elements don't disqualify: invalid markup like the org
    // templates' <rt> wrappers gets foster-parented out of tables as empty
    // debris right next to the real table.
    const tag = n.tagName.toLowerCase();
    const isEmpty = !['img', 'hr', 'br'].includes(tag)
      && !normalizeText(n.textContent).trim()
      && !(n.querySelector && n.querySelector('img,table,hr,br'));
    if (!isEmpty) return null;
  }
  return table;
}

/**
 * A layout table -> spec rows. Rows whose single cell just wraps another
 * table are unwrapped (that's how our own compiler — and most signature
 * generators — nest the column table inside a padding cell).
 */
function specRowsFromTable(tableEl, ctx, state, inheritedWidth, depth = 0) {
  const st = styleMap(tableEl);
  const attrW = tableEl.getAttribute('width');
  const tableW = parseLength(st.width, ctx.fontSize)
    || (parsePercent(attrW) == null ? parseLength(attrW, ctx.fontSize) : null)
    || parseLength(st['max-width'], ctx.fontSize)
    || inheritedWidth
    || null;
  if (tableW && !state.rootTableWidth) state.rootTableWidth = tableW;
  const cellpadding = parseInt(tableEl.getAttribute('cellpadding'), 10) || 0;
  const tableCtx = deriveCtx(ctx, tableEl, state);
  const out = [];
  let carryGap = 0; // wrapper-cell padding-bottom -> next row's spacingTop

  const pushRows = (specRows) => {
    if (carryGap && specRows.length) {
      specRows[0].spacingTop = Math.min((specRows[0].spacingTop || 0) + carryGap, LIMITS.spacing.max);
      carryGap = 0;
    }
    out.push(...specRows);
  };

  for (const tr of tableRows(tableEl)) {
    const cells = cellsOf(tr);
    if (cells.length === 1 && depth < 6) {
      const inner = soleChildTable(cells[0]);
      if (inner) {
        const cellSt = styleMap(cells[0]);
        const cellCtx = deriveCtx(tableCtx, cells[0], state);
        let padTop = parseLength(cellSt['padding-top'], ctx.fontSize);
        let padBottom = parseLength(cellSt['padding-bottom'], ctx.fontSize);
        if (cellSt.padding) {
          const parts = cellSt.padding.split(/\s+/).map((p) => parseLength(p, ctx.fontSize) || 0);
          if (padTop == null) padTop = parts[0];
          if (padBottom == null) padBottom = parts.length >= 3 ? parts[2] : parts[0];
        }
        // Social/button tables become single-column rows with that block.
        const social = detectSocialTable(inner, cellCtx, state);
        const button = social ? null : detectButtonTable(inner, cellCtx);
        if (social || button) {
          pushRows([{
            spacingTop: Math.min(padTop || 0, LIMITS.spacing.max),
            columns: [{ width: null, valign: 'top', align: 'left', paddingLeft: 0, paddingRight: 0, borderLeft: null, blocks: [social || button] }],
          }]);
          carryGap = padBottom || 0;
          continue;
        }
        const innerRows = specRowsFromTable(inner, cellCtx, state, tableW, depth + 1);
        if (innerRows.length && padTop) {
          innerRows[0].spacingTop = Math.min((innerRows[0].spacingTop || 0) + padTop, LIMITS.spacing.max);
        }
        pushRows(innerRows);
        carryGap = padBottom || 0;
        continue;
      }
    }
    pushRows([specRowFromTr(tr, tableCtx, state, tableW, cellpadding)]);
  }

  // In HTML all rows of one table share a column grid; our spec rows are
  // independent. Copy explicit widths onto width-less rows with the same
  // column count so they keep lining up.
  const widthTemplates = new Map();
  for (const sr of out) {
    const n = sr.columns.length;
    if (!widthTemplates.has(n) && sr.columns.some((c) => c.width != null)) {
      widthTemplates.set(n, sr.columns.map((c) => c.width));
    }
  }
  for (const sr of out) {
    const tmpl = widthTemplates.get(sr.columns.length);
    if (tmpl && sr.columns.every((c) => c.width == null)) {
      sr.columns.forEach((c, i) => { c.width = tmpl[i]; });
    }
  }
  return out;
}

/** One <tr> of a top-level table -> a spec row. */
function specRowFromTr(tr, ctx, state, tableWidth, cellpadding) {
  const cells = cellsOf(tr);
  let columns = [];
  const borderRights = []; // parallel to columns; applied as the NEXT column's borderLeft
  for (const cell of cells) {
    const st = styleMap(cell);
    const cellCtx = deriveCtx(ctx, cell, state);

    if (parseInt(cell.getAttribute('colspan'), 10) > 1) {
      state.warn('A cell spanning multiple columns was imported as a single column — alignment may shift slightly.');
    }
    if (parseColor(st['background-color'] || cell.getAttribute('bgcolor'))) {
      state.warn('A background color on a layout cell is not supported and was dropped.');
    }

    // Width: CSS px > width attr px > width attr % (of the table width).
    let width = parseLength(st.width, ctx.fontSize);
    if (width == null) {
      const attrRaw = cell.getAttribute('width');
      if (attrRaw != null && attrRaw !== '') {
        const pct = parsePercent(attrRaw);
        if (pct != null && tableWidth) width = Math.round(pct * tableWidth);
        else {
          const n = parseInt(attrRaw, 10);
          if (Number.isFinite(n) && n > 0) width = n;
        }
      }
    }

    const valignRaw = (cell.getAttribute('valign') || st['vertical-align'] || '').toLowerCase();
    const valign = ['top', 'middle', 'bottom'].includes(valignRaw) ? valignRaw : 'middle'; // td default

    let paddingLeft = parseLength(st['padding-left'], ctx.fontSize);
    let paddingRight = parseLength(st['padding-right'], ctx.fontSize);
    let padTop = parseLength(st['padding-top'], ctx.fontSize);
    let padBottom = parseLength(st['padding-bottom'], ctx.fontSize);
    if (st.padding) {
      const parts = st.padding.split(/\s+/).map((p) => parseLength(p, ctx.fontSize) || 0);
      const [t, r = t, b = t, l = r] = parts;
      if (padTop == null) padTop = t;
      if (paddingRight == null) paddingRight = r;
      if (padBottom == null) padBottom = b;
      if (paddingLeft == null) paddingLeft = l;
    }
    paddingLeft = (paddingLeft || 0) + cellpadding;
    paddingRight = (paddingRight || 0) + cellpadding;
    padTop = (padTop || 0) + cellpadding;
    padBottom = (padBottom || 0) + cellpadding;

    const borderLeft = parseBorder(st['border-left']);
    borderRights.push(parseBorder(st['border-right']));

    const builder = new ColumnBuilder(state, width || tableWidth);
    const divider = dividerFromEl(cell, cellCtx);
    let cellBorderBottom = null;
    if (divider) {
      if (padTop > 1) builder.addGap(padTop);
      builder.pushBlock(divider, cellCtx);
    } else {
      // Borders on a CONTENT cell render as rules above/below its content
      // (outside the padding) — emit them as divider blocks.
      const cellBorderTop = parseBorder(st['border-top']);
      cellBorderBottom = parseBorder(st['border-bottom']);
      if (cellBorderTop) builder.pushBlock({ type: 'divider', ...cellBorderTop, spacingTop: 0, spacingBottom: 0 }, cellCtx);
      if (padTop > 1) builder.addGap(padTop);
      const heightAttr = parseLength(st.height, ctx.fontSize) || parseLength(cell.getAttribute('height'), ctx.fontSize);
      const isEmpty = !normalizeText(cell.textContent).trim() && !cell.querySelector('img,table,hr');
      if (isEmpty && heightAttr) {
        builder.pushBlock({ type: 'spacer', height: Math.max(2, Math.min(heightAttr, LIMITS.spacing.max)) }, cellCtx);
      } else {
        walkFlow(cell, cellCtx, builder, state);
      }
    }
    let blocks = builder.finish();
    if (padBottom > 1 && blocks.length) {
      blocks.push({ type: 'spacer', height: Math.max(2, Math.min(padBottom, LIMITS.spacing.max)) });
    }
    if (cellBorderBottom && blocks.length) {
      blocks.push({ type: 'divider', ...cellBorderBottom, spacingTop: 0, spacingBottom: 0 });
    }
    blocks = mergeOverflowBlocks(blocks, state);

    columns.push({
      width: width || null,
      valign,
      align: 'left',
      paddingLeft: Math.min(paddingLeft, LIMITS.spacing.max),
      paddingRight: Math.min(paddingRight, LIMITS.spacing.max),
      borderLeft,
      blocks,
    });
  }
  // A cell's right border and the next cell's left border draw the same
  // vertical rule — the spec only models borderLeft, so shift it over.
  borderRights.forEach((br, i) => {
    if (br && columns[i + 1] && !columns[i + 1].borderLeft) columns[i + 1].borderLeft = br;
  });
  if (columns.length > LIMITS.maxColumnsPerRow) {
    // Never drop real content to fit the column cap: sacrifice spacer-only
    // hack cells first, then stack whatever still overflows into the last
    // kept column (below its content) instead of losing it.
    const isSpacerOnly = (col) => col.blocks.length > 0 && col.blocks.every((b) => b.type === 'spacer');
    let kept = columns.filter((col) => col.blocks.length > 0 && !isSpacerOnly(col));
    if (!kept.length) kept = columns.slice(0, LIMITS.maxColumnsPerRow);
    if (kept.length > LIMITS.maxColumnsPerRow) {
      const overflow = kept.slice(LIMITS.maxColumnsPerRow);
      kept = kept.slice(0, LIMITS.maxColumnsPerRow);
      const last = kept[kept.length - 1];
      for (const col of overflow) last.blocks.push(...col.blocks);
      last.blocks = mergeOverflowBlocks(last.blocks, state);
      state.warn(`A table row had ${cells.length} columns — the extra content was stacked into the last column. Review that section.`);
    } else {
      state.warn(`A table row had ${cells.length} columns — empty filler cells were dropped to fit the Studio's ${LIMITS.maxColumnsPerRow}-column layout.`);
    }
    columns = kept;
  }
  return { spacingTop: 0, columns };
}

/** Merge identically-styled adjacent text lines when a column exceeds the cap. */
function mergeOverflowBlocks(blocks, state) {
  if (blocks.length <= LIMITS.maxBlocksPerColumn) return blocks;
  const canMerge = (a, b) => a && b && a.type === 'text' && b.type === 'text'
    && !a.segments && !b.segments && !a.href && !b.href
    && a.bold === b.bold && a.italic === b.italic && a.underline === b.underline
    && (a.color || '') === (b.color || '') && (a.size || 0) === (b.size || 0)
    && (b.spacingTop || 0) === 0 && !a.align && !b.align
    // Never merge past the sanitize cap — that would truncate text.
    && a.text.length + b.text.length + 1 <= LIMITS.textLength;
  const out = [];
  for (const block of blocks) {
    const prev = out[out.length - 1];
    if (prev && canMerge(prev, block)) prev.text = `${prev.text}\n${block.text}`;
    else out.push(block);
  }
  if (out.length > LIMITS.maxBlocksPerColumn) {
    state.warn(`A section had more content than the Studio supports — it was trimmed to ${LIMITS.maxBlocksPerColumn} blocks.`);
    return out.slice(0, LIMITS.maxBlocksPerColumn);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Global style inference
// ---------------------------------------------------------------------------

function topKey(map) {
  let best = null;
  let bestN = 0;
  for (const [k, n] of map) {
    if (n > bestN) { best = k; bestN = n; }
  }
  return best;
}

function inferGlobals(spec, state) {
  const g = defaultGlobal();

  const fontId = topKey(state.fontTallyMap);
  if (fontId && fontId !== DEFAULT_KEY) g.fontId = fontId;

  const domSize = topKey(state.sizeTallyMap);
  if (domSize && domSize !== DEFAULT_KEY) {
    g.fontSize = Math.min(Math.max(domSize, LIMITS.fontSize.min), LIMITS.fontSize.max);
  }

  // Tallies hold EFFECTIVE colors (unstyled content tallied at Gmail's
  // rendered defaults), so the top key is exactly what recipients saw most.
  const domColor = topKey(state.textColorTallyMap);
  if (domColor) g.textColor = domColor;
  const domLink = topKey(state.linkColorTallyMap);
  if (domLink) g.linkColor = domLink;

  // maxWidth: root table width if known, else wide enough for the content.
  let widest = 0;
  for (const row of spec.rows) {
    let rowWidth = 0;
    for (const col of row.columns) {
      for (const block of col.blocks) {
        if (block.type === 'image' && block.width) widest = Math.max(widest, block.width);
      }
      rowWidth += (col.width || 0) + (col.paddingLeft || 0) + (col.paddingRight || 0);
    }
    widest = Math.max(widest, rowWidth);
  }
  let maxWidth = state.rootTableWidth || Math.max(widest, 520);
  maxWidth = Math.max(maxWidth, widest);
  g.maxWidth = Math.min(Math.max(maxWidth, LIMITS.maxWidth.min), LIMITS.maxWidth.max);

  // Normalize every text run's EFFECTIVE color/size against the inferred
  // globals: values matching the global are dropped (they inherit), values
  // that differ are made explicit — so the compiled output is identical to
  // the original no matter which style "won" the global slot.
  for (const row of spec.rows) {
    for (const col of row.columns) {
      for (const block of col.blocks) {
        if (block.type !== 'text') continue;
        if (Array.isArray(block.segments) && block.segments.length) {
          for (const seg of block.segments) {
            const effective = seg.color || (seg.href ? GMAIL_DEFAULT_LINK : GMAIL_DEFAULT_TEXT);
            const globalDefault = seg.href ? g.linkColor : g.textColor;
            if (effective === globalDefault) delete seg.color;
            else seg.color = effective;
          }
          if (block.size === g.fontSize) block.size = null;
        } else {
          const effective = block.color || (block.href ? GMAIL_DEFAULT_LINK : GMAIL_DEFAULT_TEXT);
          const globalDefault = block.href ? g.linkColor : g.textColor;
          block.color = effective === globalDefault ? '' : effective;
          if (block.size === g.fontSize) block.size = null;
        }
      }
    }
  }

  spec.global = g;
}

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

function defaultParseDocument(html) {
  if (typeof DOMParser !== 'undefined') {
    return new DOMParser().parseFromString(html, 'text/html');
  }
  throw new Error('No DOM parser available — pass options.parseDocument (e.g. from jsdom).');
}

function makeState(warnings) {
  const seen = new Set();
  return {
    warn(msg) {
      if (!seen.has(msg)) { seen.add(msg); warnings.push(msg); }
    },
    pendingImages: [],
    /** Natural-size resolution mutates its target — repoint it when an image block becomes an inline segment. */
    retargetPending(oldTarget, newTarget) {
      for (const p of this.pendingImages) {
        if (p.block === oldTarget) p.block = newTarget;
      }
    },
    fontTallyMap: new Map(),
    sizeTallyMap: new Map(),
    textColorTallyMap: new Map(),
    linkColorTallyMap: new Map(),
    fontFallbacks: new Set(),
    rootTableWidth: null,
    tallyFont(fontId, weight) {
      if (!weight) return;
      const key = fontId || DEFAULT_KEY;
      this.fontTallyMap.set(key, (this.fontTallyMap.get(key) || 0) + weight);
    },
    tallySize(size, weight) {
      if (!weight) return;
      const key = size || DEFAULT_KEY;
      this.sizeTallyMap.set(key, (this.sizeTallyMap.get(key) || 0) + weight);
    },
    tallyTextColor(color, weight) {
      if (!weight) return;
      const key = color || DEFAULT_KEY;
      this.textColorTallyMap.set(key, (this.textColorTallyMap.get(key) || 0) + weight);
    },
    tallyLinkColor(color, weight) {
      if (!weight) return;
      const key = color || DEFAULT_KEY;
      this.linkColorTallyMap.set(key, (this.linkColorTallyMap.get(key) || 0) + weight);
    },
    noteFontFallback(family) {
      const first = String(family || '').split(',')[0].trim().replace(/^['"]|['"]$/g, '');
      if (first && !this.fontFallbacks.has(first.toLowerCase())) {
        this.fontFallbacks.add(first.toLowerCase());
        this.warn(`The font "${first}" isn't email-safe — the closest web-safe font is used instead (most recipients were already seeing the fallback).`);
      }
    },
  };
}

/**
 * Parse signature HTML into an (unresolved) spec. Synchronous — image blocks
 * whose dimensions could not be determined from the markup are listed in
 * `pendingImages` and get finalized by resolveImageDimensions().
 *
 * Returns { ok, spec?, warnings, pendingImages?, error? }.
 */
export function parseSignatureHtml(html, options = {}) {
  const raw = String(html || '').trim();
  if (!raw) return { ok: false, error: 'There is no signature to import.', warnings: [] };

  let doc;
  try {
    doc = (options.parseDocument || defaultParseDocument)(raw);
  } catch (err) {
    return { ok: false, error: `Could not parse the signature HTML: ${err.message}`, warnings: [] };
  }
  const body = doc && (doc.body || doc.documentElement);
  if (!body) return { ok: false, error: 'Could not parse the signature HTML.', warnings: [] };

  const warnings = [];
  const state = makeState(warnings);
  const rows = [];

  /** Non-table content pools into a single-column row between tables. */
  let pool = null;
  const flushPool = () => {
    if (!pool) return;
    const blocks = mergeOverflowBlocks(pool.finish(), state);
    if (blocks.length) {
      rows.push({ spacingTop: 0, columns: [{ width: null, valign: 'top', align: 'left', paddingLeft: 0, paddingRight: 0, borderLeft: null, blocks }] });
    }
    pool = null;
  };
  const poolBuilder = () => {
    if (!pool) pool = new ColumnBuilder(state, state.rootTableWidth);
    return pool;
  };

  const containsTable = (el) => Boolean(el.querySelector && el.querySelector('table'));

  const visit = (node, ctx) => {
    if (node.nodeType === 3) {
      if (normalizeText(node.textContent).trim()) poolBuilder().addText(node.textContent, ctx);
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();
    if (SKIP_TAGS.has(tag)) return;

    if (tag === 'table') {
      // Social bars / buttons stay blocks in the pooled column.
      const social = detectSocialTable(node, ctx, state);
      const button = social ? null : detectButtonTable(node, ctx);
      if (social || button) {
        const b = poolBuilder();
        b.endLine();
        b.pushBlock(social || button, ctx);
        return;
      }
      flushPool();
      rows.push(...specRowsFromTable(node, ctx, state, state.rootTableWidth));
      return;
    }

    const childCtx = deriveCtx(ctx, node, state);

    // Wrapper elements around table layout (Gmail wraps everything in divs):
    // recurse at top level so inner tables still become spec rows.
    if (isBlockEl(node) && containsTable(node)) {
      if (pool) pool.endLine();
      const margins = blockMargins(node, ctx);
      if (margins.top) poolBuilder().addGap(margins.top);
      for (const child of Array.from(node.childNodes)) visit(child, childCtx);
      if (pool) pool.endLine();
      if (margins.bottom) poolBuilder().addGap(margins.bottom);
      return;
    }

    const builder = poolBuilder();
    if (tag === 'br') { builder.endLine(true); return; }
    if (tag === 'img') { builder.addImage(node, ctx); return; }
    if (tag === 'hr') {
      builder.endLine();
      builder.pushBlock(dividerFromEl(node, ctx), ctx);
      return;
    }
    if (isButtonLink(node)) {
      builder.endLine();
      builder.pushBlock(buttonFromLink(node, childCtx, state), ctx);
      return;
    }
    if (isBlockEl(node)) {
      builder.endLine();
      const margins = blockMargins(node, ctx);
      if (margins.top) builder.addGap(margins.top);
      const divider = dividerFromEl(node, childCtx);
      if (divider) {
        builder.pushBlock(divider, childCtx);
      } else {
        const st = styleMap(node);
        const explicitH = parseLength(st.height, ctx.fontSize);
        const isEmpty = !normalizeText(node.textContent).trim() && !(node.querySelector && node.querySelector('img,hr'));
        if (isEmpty) {
          builder.addGap(explicitH || childCtx.lineHeight || Math.round(childCtx.fontSize * 1.2));
        } else {
          walkFlow(node, childCtx, builder, state);
          builder.endLine();
        }
      }
      if (margins.bottom) builder.addGap(margins.bottom);
      return;
    }
    // Inline content at the top level.
    walkFlow(node, childCtx, builder, state);
  };

  for (const node of Array.from(body.childNodes)) visit(node, rootCtx());
  flushPool();

  const hasContent = rows.some((r) => r.columns.some((c) => c.blocks.length));
  if (!hasContent) {
    return { ok: false, error: 'The current signature has no importable content.', warnings };
  }
  if (rows.length > LIMITS.maxRows) {
    state.warn(`The signature had ${rows.length} sections — only the first ${LIMITS.maxRows} were kept.`);
    rows.length = LIMITS.maxRows;
  }

  const spec = {
    version: 1,
    name: 'Imported from Gmail',
    global: defaultGlobal(),
    rows,
  };
  inferGlobals(spec, state);

  return { ok: true, spec, warnings, pendingImages: state.pendingImages };
}

/** Default browser image measurer (natural size), 8s timeout, never rejects. */
export function browserLoadImage(src) {
  return new Promise((resolve) => {
    if (typeof Image === 'undefined') { resolve(null); return; }
    let done = false;
    const finish = (v) => { if (!done) { done = true; resolve(v); } };
    const img = new Image();
    const timer = setTimeout(() => finish(null), 8000);
    img.onload = () => { clearTimeout(timer); finish({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { clearTimeout(timer); finish(null); };
    img.src = src;
  });
}

/**
 * Fill in dimensions the markup didn't specify, using each image's natural
 * size — required for "looks 100% the same": a dimensionless <img> renders at
 * natural size, so the reconstructed spec must carry those exact pixels.
 */
export async function resolveImageDimensions(spec, pendingImages, loadImage, warnings) {
  const warn = (msg) => { if (!warnings.includes(msg)) warnings.push(msg); };
  await Promise.all((pendingImages || []).map(async (pending) => {
    const { block } = pending;
    let natural = null;
    try {
      natural = await loadImage(block.src);
    } catch (e) {
      natural = null;
    }
    if (!natural || !natural.width || !natural.height) {
      // Leave dimensions null: the compiler omits them and clients render the
      // natural size — same as the original markup did.
      if (block.width == null && block.height == null && !pending.percent && !pending.maxWidth) {
        warn('An image could not be measured — it will render at its natural size, exactly like the original.');
      } else if (pending.maxWidth) {
        block.width = pending.maxWidth; // best available approximation
        warn('An image with only a max-width could not be measured — its width was set to that maximum.');
      }
      return;
    }
    const ratio = natural.height / natural.width;
    if (pending.percent != null) {
      const container = spec.global.maxWidth || 520;
      block.width = Math.max(1, Math.round(pending.percent * container));
      block.height = Math.max(1, Math.round(block.width * ratio));
      warn('An image sized with a percentage width was converted to fixed pixels (email clients need fixed sizes).');
      return;
    }
    if (pending.maxWidth) {
      block.width = Math.min(natural.width, pending.maxWidth);
      block.height = Math.max(1, Math.round(block.width * ratio));
      return;
    }
    if (block.width == null && block.height == null) {
      block.width = natural.width;
      block.height = natural.height;
    } else if (block.width == null) {
      block.width = Math.max(1, Math.round(block.height / ratio));
    } else if (block.height == null) {
      block.height = Math.max(1, Math.round(block.width * ratio));
    }
  }));

  // Oversized images: widen the canvas up to the Gmail-safe max, then scale.
  let widest = 0;
  for (const row of spec.rows) {
    for (const col of row.columns) {
      for (const block of col.blocks) {
        if (block.type === 'image' && block.width) widest = Math.max(widest, block.width);
      }
    }
  }
  if (widest > spec.global.maxWidth) {
    spec.global.maxWidth = Math.min(widest, LIMITS.maxWidth.max);
  }
  for (const row of spec.rows) {
    for (const col of row.columns) {
      for (const block of col.blocks) {
        if (block.type === 'image' && block.width > LIMITS.maxWidth.max) {
          const scale = LIMITS.maxWidth.max / block.width;
          block.width = LIMITS.maxWidth.max;
          if (block.height) block.height = Math.max(1, Math.round(block.height * scale));
          warn(`An image wider than ${LIMITS.maxWidth.max}px was scaled down to the email-safe maximum.`);
        }
      }
    }
  }
}

/**
 * Merge adjacent identically-styled single-line text blocks into one
 * multi-line block (rendered with <br> — visually identical) so the compiled
 * HTML gets leaner. Only applied when the output would exceed Gmail's limit:
 * our div-per-line markup can be slightly heavier than the original's nested
 * inheritance, and an imported signature must stay installable.
 */
function compactSpec(spec) {
  // 1. Drop empty columns that occupy no space (no blocks, border, or width).
  for (const row of spec.rows) {
    row.columns = row.columns.filter((c) => c.blocks.length || c.borderLeft || c.width);
  }

  // 2. Rows containing ONLY spacers are pure vertical whitespace — fold them
  //    into the next row's spacingTop instead of paying for a whole table.
  for (let i = spec.rows.length - 1; i >= 0; i -= 1) {
    const row = spec.rows[i];
    if (!row.columns.length) { spec.rows.splice(i, 1); continue; }
    const onlySpacers = row.columns.every((c) => c.blocks.length && c.blocks.every((b) => b.type === 'spacer'));
    if (!onlySpacers) continue;
    const gap = Math.max(...row.columns.map((c) => c.blocks.reduce((s, b) => s + (b.height || 0), 0)));
    const next = spec.rows[i + 1];
    if (next) next.spacingTop = Math.min((next.spacingTop || 0) + (row.spacingTop || 0) + gap, LIMITS.spacing.max);
    spec.rows.splice(i, 1);
  }

  // 3. Merge adjacent identically-styled plain text lines into one block.
  const canMerge = (a, b) => a && b && a.type === 'text' && b.type === 'text'
    && !a.segments && !b.segments && !a.href && !b.href
    && a.bold === b.bold && a.italic === b.italic && a.underline === b.underline
    && (a.color || '') === (b.color || '') && (a.size || 0) === (b.size || 0)
    && (a.lineHeight || 0) === (b.lineHeight || 0)
    && (a.align || '') === (b.align || '')
    && (a.letterSpacing || 0) === (b.letterSpacing || 0)
    && (b.spacingTop || 0) === 0
    && a.text.length + b.text.length + 1 <= LIMITS.textLength;
  for (const row of spec.rows) {
    for (const col of row.columns) {
      const out = [];
      for (const block of col.blocks) {
        const prev = out[out.length - 1];
        if (prev && canMerge(prev, block)) prev.text = `${prev.text}\n${block.text}`;
        else out.push(block);
      }
      col.blocks = out;
    }
  }

  // 4. Merge consecutive full-width single-column rows with identical column
  //    chrome — a stack of one-column tables renders exactly like one column.
  const chrome = (c) => `${c.align || 'left'}|${c.valign || 'top'}|${c.paddingLeft || 0}|${c.paddingRight || 0}`;
  for (let i = spec.rows.length - 2; i >= 0; i -= 1) {
    const a = spec.rows[i];
    const b = spec.rows[i + 1];
    if (a.columns.length !== 1 || b.columns.length !== 1) continue;
    const ca = a.columns[0];
    const cb = b.columns[0];
    if (ca.width || cb.width || ca.borderLeft || cb.borderLeft) continue;
    if (chrome(ca) !== chrome(cb)) continue;
    if (ca.blocks.length + cb.blocks.length > LIMITS.maxBlocksPerColumn) continue;
    if (cb.blocks[0]) {
      cb.blocks[0].spacingTop = Math.min((cb.blocks[0].spacingTop || 0) + (b.spacingTop || 0), LIMITS.spacing.max);
    }
    ca.blocks.push(...cb.blocks);
    spec.rows.splice(i + 1, 1);
  }
}

/**
 * Full import: parse -> resolve image dimensions -> sanitize into a spec
 * ready for the Studio. Returns { ok, spec?, warnings, error? }.
 */
export async function importSignatureHtml(html, options = {}) {
  const parsed = parseSignatureHtml(html, options);
  if (!parsed.ok) return parsed;
  const loadImage = options.loadImage || browserLoadImage;
  await resolveImageDimensions(parsed.spec, parsed.pendingImages, loadImage, parsed.warnings);
  const sanitized = sanitizeSpec(parsed.spec);
  if (!sanitized.ok) return { ok: false, error: sanitized.error, warnings: parsed.warnings };
  let spec = sanitized.spec;

  if (getSignatureStats(spec).overLimit) {
    compactSpec(spec);
    const again = sanitizeSpec(spec);
    if (again.ok) spec = again.spec;
    if (getSignatureStats(spec).overLimit) {
      parsed.warnings.push("The signature is over Gmail's 10,000-character limit — remove or shorten something before installing.");
    }
  }

  spec.name = 'Imported from Gmail';
  return { ok: true, spec, warnings: parsed.warnings };
}
